import useGetTask from '@/api/queries/tasks/useGetTask';
import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import CreateEditTaskSidebar from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';
import { getDeal, getTask } from '@/utils/stateAccessors';
import QuickActionsElement from '@payaca/components/quickActionsTableCell/QuickActionsElement';
import UserIndicator from '@payaca/components/userIndicator/UserIndicator';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { requestDeleteTask } from '@payaca/store/tasks/tasksActions';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import TeamAssigneeBadge from '../taskAssignee/TeamAssigneeBadge';
import './TaskDetailsAndActionsHeader.sass';

type Props = {
  taskId: number;
  hideDealLink?: boolean;
  onDeleteTaskSuccess?: () => void;
  onUpdateTaskSuccess?: () => void;
};
const TaskDetailsAndActionsHeader: FC<Props> = ({
  taskId,
  hideDealLink,
  onDeleteTaskSuccess,
  onUpdateTaskSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);

  const task = useSelector((state) => getTask(state, taskId));
  const profile = useSelector((state: any) => state.users.myProfile);

  const linkedDeal = useSelector((state) =>
    task?.dealId ? getDeal(state, task.dealId) : undefined
  );

  const { userHasRequiredPermission } = usePermissions();

  // The redux/REST task carries only `assignedToUserId`, so a Team assignee is
  // invisible to it. Read the assignee from GraphQL, where it is a User | Team
  // union.
  const { task: assignableTask } = useGetTask(taskId);
  const assignedTo = assignableTask?.assignedTo;

  const deleteTask = useCallback(() => {
    if (task) {
      dispatch(requestDeleteTask(taskId, onDeleteTaskSuccess));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'task'
  }, [onDeleteTaskSuccess, dispatch, taskId]);

  const isDeletingTask = useSelector((state) => {
    return state.tasks.isDeletingTask;
  });

  const quickActions = useMemo(() => {
    const quickActions: any[] = [];

    if (task?.type === 'materials-list') {
      return quickActions;
    }

    if (
      task?.type !== 'form' &&
      (userHasRequiredPermission([TasksPermissions.PERSIST_TASK]) ||
        (userHasRequiredPermission([
          TasksPermissions.PERSIST_SELF_CREATED_TASK,
        ]) &&
          task?.createdByUserId == profile.id))
    ) {
      quickActions.push({
        actionName: 'Edit',
        actionBehaviour: () => setShowEditTaskModal(true),
      });
    }

    if (
      userHasRequiredPermission([TasksPermissions.DELETE_TASK]) ||
      (userHasRequiredPermission([TasksPermissions.DELETE_SELF_CREATED_TASK]) &&
        task?.createdByUserId == profile.id)
    ) {
      quickActions.push({
        isProcessing: isDeletingTask,
        actionName: 'Delete',
        actionBehaviour: deleteTask,
      });
    }

    return quickActions;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'task.type', and 'task.createdByUserId'
  }, [userHasRequiredPermission, isDeletingTask, deleteTask, profile]);

  const dealLinkElement = useMemo(() => {
    if (!linkedDeal || hideDealLink) return;
    return (
      <PermissionGuard
        renderIfHasPermissions={[
          DealsPermissions.GET_DEAL,
          DealsPermissions.GET_MY_DEAL,
        ]}
      >
        <div className="grow">
          <Link
            to={`/projects/${linkedDeal.publicId}`}
            className="rounded-md bg-blue-100 px-2 py-1 font-semibold text-blue-800 hover:bg-blue-200 hover:no-underline"
          >
            Project {linkedDeal.customReference || linkedDeal.reference}
          </Link>
        </div>
      </PermissionGuard>
    );
  }, [linkedDeal, hideDealLink]);

  const assigneeElement = useMemo(() => {
    if (!assignedTo) return;
    return (
      <PermissionGuard
        renderIfHasPermissions={[
          AccountsPermissions.GET_USERS,
          TasksPermissions.GET_TASKS,
        ]}
        requireAllPermissions={true}
      >
        <div>
          {assignedTo.__typename === 'Team' ? (
            <TeamAssigneeBadge team={assignedTo} />
          ) : (
            <UserIndicator
              user={{
                firstName: assignedTo.firstName || '',
                lastName: assignedTo.lastName || '',
                emailAddress: assignedTo.email || '',
                userColour: assignedTo.colour || '',
                imgSrc: assignedTo.avatarUrl ?? undefined,
              }}
            />
          )}
        </div>
      </PermissionGuard>
    );
  }, [assignedTo]);

  const quickActionsElement = useMemo(() => {
    return (
      <QuickActionsElement recordId={taskId} quickActions={quickActions} />
    );
  }, [taskId, quickActions]);

  if (!task) return null;

  if (!dealLinkElement && !assigneeElement && !quickActionsElement) return null;

  return (
    <>
      <div
        className={
          'task-details-and-actions-container flex space-x-3.5 items-center'
        }
      >
        {dealLinkElement}
        {assigneeElement}
        {quickActionsElement}
      </div>

      <CreateEditTaskSidebar
        isOpen={showEditTaskModal}
        taskId={task.id}
        projectId={task.dealId}
        onClose={() => setShowEditTaskModal(false)}
        onSuccess={() => {
          onUpdateTaskSuccess?.();
        }}
      />
    </>
  );
};
export default TaskDetailsAndActionsHeader;
